import { startupTimer } from '@/app/gridGL/helpers/startupTimer';
import { trackEvent } from '@/shared/utils/analyticsEvents';
import { useLayoutEffect } from 'react';

// Module-level guard so `trackInitialLoadTiming` is idempotent across the
// app lifetime. Needed because React StrictMode mounts effects twice (mount,
// unmount, remount), and `useTrackInitialLoadTiming` does NOT clear
// `data-loading-start` (the file viewer keeps the shell visible until the
// `<FileLoadingOverlay>` completion flash finishes). Without this guard the
// second mount would call `startupTimer.start('firstRender')` again.
let didTrackInitialLoadTiming = false;

const trackInitialLoadTiming = () => {
  if (didTrackInitialLoadTiming) return;

  // Read the start time set by index.html before potentially clearing it.
  // Subsequent calls (after another route already removed the attribute)
  // are naturally no-ops.
  const startTime = document.documentElement.getAttribute('data-loading-start');
  if (!startTime) return;
  didTrackInitialLoadTiming = true;

  startupTimer.start('firstRender', Number(startTime));
  startupTimer.end('firstRender');
  const timers = startupTimer.show();
  if (timers) {
    trackEvent('[Loading].complete', {
      route: window.location.pathname + window.location.search,
      ...timers,
    });
  }
};

/**
 * Removes the initial HTML loading shell (the `data-loading-start` attribute
 * on `<html>` set by index.html) and reports load timing telemetry.
 *
 * Use this on routes that don't go through the file-loading flow (dashboard,
 * empty/error pages, embed import, etc.) — they own when the shell should
 * disappear. The file viewer route should NOT call this; the
 * `<FileLoadingOverlay>` controls the shell's lifecycle for files so the
 * gray logo + gif keeps painting through the entire load (including the
 * progress-bar completion flash) without flickering.
 */
export function useRemoveInitialLoadingUI(skip: boolean = false) {
  useLayoutEffect(() => {
    if (skip) return;
    trackInitialLoadTiming();
    document.documentElement.removeAttribute('data-loading-start');
  }, [skip]);
  return null;
}

/**
 * Reports load timing telemetry without tearing down the loading shell.
 * Used by the file viewer (`QuadraticUI`) so the
 * `<FileLoadingOverlay>` can keep the shell visible until its progress-bar
 * completion flash finishes, then remove the attribute itself.
 */
export function useTrackInitialLoadTiming(skip: boolean = false) {
  useLayoutEffect(() => {
    if (skip) return;
    trackInitialLoadTiming();
  }, [skip]);
  return null;
}
